import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Dialog,
  Grid,
  Typography,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TablePagination,
} from "@mui/material";
import styles from "../../../styles.module.css";
import { getDeliveryChallanList, editDeleteDeliveryChallan } from "../../../actions/actions";
import {
  Breadcrumb,
  CustomePopover,
  DeleteDialog,
  SearchInput,
  StyledTableCell,
  StyledTableRow,
  useMessageHandler,
} from "../../reusableComponent/Reusables";
import { CancelDialog } from "../../reusableComponent/Reusables";
import ReusableSnackbar from "../../reusableComponent/ReusableSnackbar";
import CloseIcon from "@mui/icons-material/Close";
import deliveryChallanService from "../../../services/deliveryChallan.Service";
import HeaderSection from "../../reusableComponent/HeaderSection";
import DeliveryChallanPrint from "./DeliveryChallanPrint";
import CustomPagination from "../../reusableComponent/CustomPagination";

const DeliveryChallanList = () => {
  const dispatch = useDispatch();
  const login_details = sessionStorage.getItem("loginUser");
  const userD = JSON.parse(login_details);
  const userInfo = userD.result.userInfo;
  const depot_id = userInfo.depot_id || 0;

  const [isDelete, setIsDelete] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState(null);
  const [isCancel, setIsCancel] = useState(false);
  const [cancelInfo, setCancelInfo] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [rows, setRows] = useState([]);
  const [searchKey, setSearchKey] = useState("");
  const [openCylinderDialog, setOpenCylinderDialog] = useState(false);
  const [selectedCylinderDetails, setSelectedCylinderDetails] = useState([]);
  const [dialogTitle, setDialogTitle] = useState("Cylinder Details");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [total_count, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const totalCount = useSelector((state) => state.delivery_challan.total_count || 0);
  const defaultRowsPerPage = useSelector((state) => state.delivery_challan.rows_per_page || 15);

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: defaultRowsPerPage,
  });

  const dateConversion = (date) => {
    if (!date || isNaN(new Date(date))) return "N/A";
    const today = new Date(date);
    const dd = String(today.getDate()).padStart(2, "0");
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const yyyy = today.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  useEffect(() => {
    console.log("isCancel:", isCancel, "cancelInfo:", cancelInfo);
  }, [isCancel, cancelInfo]);

  useEffect(() => {
    setController((prev) => ({
      ...prev,
      rowsPerPage: defaultRowsPerPage,
      page: 0,
    }));
  }, [defaultRowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setController({
      ...controller,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0,
    });
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: controller.page,
        size: controller.rowsPerPage,
        searchKey: searchKey,
        depot_id,
      };
      const response = await deliveryChallanService.getDeliveryChallanList(params);
      console.log("Fetched rows with status:", response.data);
      const fetchedRows = (response.data || (Array.isArray(response) ? response : [])).map((row) => ({
        ...row,
        status: row.status || "active", // Preserve server-provided status
      }));
      setRows(fetchedRows);
      console.log("Updated rows state:", fetchedRows);
      setTotalCount(response.total_count || response.data?.length || response.length || 0);
    } catch (error) {
      console.error("Error fetching delivery challan list:", error);
      setSuccessMessage("Error fetching data");
      setRows([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [controller, searchKey, depot_id]);

  const handleSearch = async (e) => {
    const searchValue = e.target.value;
    setSearchKey(searchValue);
    setController((prev) => ({ ...prev, page: 0 }));
  };

  const deleteRecord = async (id) => {
    try {
      await deliveryChallanService.editDeleteDeliveryChallan({
        entry_id: id,
        changedUpdatedValue: "delete",
      });
      setIsDelete(false);
      fetchData();
      setSuccessMessage("Record deleted successfully");
    } catch (error) {
      console.error("Error deleting record:", error);
      setSuccessMessage("Error deleting record");
    }
  };

const cancelRecord = async (id, reason) => {
  try {
    const response = await deliveryChallanService.editDeleteDeliveryChallanUnbook({
      entry_id: id,
      changedUpdatedValue: "cancel",
      modified_by: userInfo.user_id,
      reason: reason 
    });

    console.log("Cancel API response:", response);

    setIsCancel(false);
    setCancelInfo(null);

    setTimeout(() => {
      fetchData();
    }, 1000);

    setSuccessMessage("Record cancelled successfully");
  } catch (error) {
    console.error("Error cancelling record:", error);

    setRows((prevRows) =>
      prevRows.map((row) =>
        row.entry_id === id ? { ...row, status: "active" } : row
      )
    );

    setSuccessMessage("Error cancelling record");
  }
};


  const handleDelete = (row) => {
    console.log("handleDelete called with data:", row);
    setIsDelete(true);
    setDeleteInfo(row);
  };

  const handleCancel = (row) => {
    console.log("handleCancel called with data:", row);
    setIsCancel(true);
    setCancelInfo(row);
  };

  const handleCloseCancel = () => {
    setIsCancel(false);
    setCancelInfo(null);
  };

  const handleClose = () => {
    setIsDelete(false);
    setDeleteInfo(null);
  };

  const [successMessage, setSuccessMessage] = useState(null);
  const respMsgState = useSelector((state) => state.delivery_challan.message);
  const { responseMessage } = useMessageHandler(fetchData, null, setSuccessMessage, respMsgState);

  useEffect(() => {
    if (respMsgState) {
      setSuccessMessage(respMsgState);
    }
  }, [respMsgState]);

  const handleOpenCylinderDialog = (row) => {
    try {
      const details = JSON.parse(row.table_details || "[]");
      setSelectedCylinderDetails(details);
      setDialogTitle(`Cylinder Details for DC No: ${row.entry_no}`);
      setOpenCylinderDialog(true);
    } catch (error) {
      console.error("Error parsing table_details:", error);
      setSelectedCylinderDetails([]);
      setDialogTitle("Cylinder Details");
      setOpenCylinderDialog(true);
    }
  };

  const handleCloseCylinderDialog = () => {
    setOpenCylinderDialog(false);
    setSelectedCylinderDetails([]);
    setDialogTitle("Cylinder Details");
  };

  const HandleSort = (field) => {
    const newSortBy = sortBy === field ? null : field;
    setSortBy(newSortBy);
    if (newSortBy) {
      setRows((prevRows) =>
        [...prevRows].sort((a, b) => {
          if (a[newSortBy] < b[newSortBy]) return -1;
          if (a[newSortBy] > b[newSortBy]) return 1;
          return 0;
        })
      );
    } else {
      fetchData();
    }
  };

  const getCylinderCount = (tableDetails) => {
    try {
      const details = JSON.parse(tableDetails || "[]");
      return details.length || 0;
    } catch (error) {
      console.error("Error parsing table_details:", error);
      return 0;
    }
  };

  const calculateTotalCylinders = (tableDetails) => {
    try {
      const details = JSON.parse(tableDetails || "[]");
      return details.filter((item) => item.cyl_no && item.cyl_no.trim() !== "").length;
    } catch {
      return 0;
    }
  };

  const calculateTotalQuantity = (tableDetails) => {
    try {
      const details = JSON.parse(tableDetails || "[]");
      return details.reduce((total, item) => {
        const qty = parseFloat(item.qty) || 0;
        return total + qty;
      }, 0);
    } catch {
      return 0;
    }
  };

  const getProductCodesSummaryForRow = (tableDetails) => {
  try {
    const details = JSON.parse(tableDetails || "[]");
    const codes = [...new Set(details.map((c) => c.prod_code).filter(Boolean))];
    return codes.length > 0 ? codes.join(", ") : "N/A";
  } catch {
    return "N/A";
  }
};

  const calculateProductSummary = (tableDetails) => {
    try {
      const details = JSON.parse(tableDetails || "[]");
      const productCounts = {};
      details.forEach((item) => {
        if (item.product_code && item.product_code.trim() !== "") {
          productCounts[item.product_code] = (productCounts[item.product_code] || 0) + 1;
        }
      });
      return Object.entries(productCounts)
        .map(([product, count]) => `${product}: ${count}`)
        .join(", ");
    } catch {
      return "N/A";
    }
  };

  const handleCylinderCountClick = (row) => {
    try {
      const details = JSON.parse(row.table_details || "[]");
      setSelectedCylinderDetails(details);
      setDialogTitle(`Cylinder Details for ${row.entry_no}`);
      setOpenCylinderDialog(true);
    } catch (error) {
      console.error("Error parsing table_details:", error);
      setSuccessMessage("Error loading cylinder details");
    }
  };

  const handleOpenDialog = (row) => {
    setSelectedRow(row);
    setSelectedCylinderDetails(safeParse(row.table_details));
    setOpenDialog(true);
  };

  const safeParse = (jsonString) => {
    try {
      return JSON.parse(jsonString || "[]");
    } catch (e) {
      return [];
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRow(null);
    setSelectedCylinderDetails([]);
  };

  const sortedRows = [...rows].sort((a, b) => new Date(b.entry_date) - new Date(a.entry_date));
  const effectiveTotalCount = total_count > 0 ? total_count : rows.length > 0 ? rows.length : 0;
  const startRecord = effectiveTotalCount === 0 ? 0 : controller.page * controller.rowsPerPage + 1;
  const endRecord = effectiveTotalCount === 0 ? 0 : Math.min((controller.page + 1) * controller.rowsPerPage, effectiveTotalCount);
  const paginationInfo = `${startRecord}-${endRecord} of ${effectiveTotalCount}`;

  return (
    <Box className={`${styles.cts_page}`} sx={{ padding: "0px !important", margin: "0px !important" }}>
      <Box className={`${styles.body}`}>
        <Breadcrumb routeSegments={[{ name: "Delivery Challan List" }]} />
        <HeaderSection
          title="DELIVERY CHALLAN LIST"
          addNewPath="/LogisticDistribution/add-dc"
          searchKey={searchKey}
          handleSearch={handleSearch}
          sortedRows={sortedRows.map((row) => ({
            ...row,
            totalCylinders: calculateTotalCylinders(row.table_details),
            totalQuantity: calculateTotalQuantity(row.table_details),
            productsSummary: calculateProductSummary(row.table_details),
          }))}
          fields={["entry_no", "entry_date", "type", "table_details", "vehicle_no", "driver_name"]}
          labels={["DC No", "DC Date", "Party Name", "No Of Cylinder", "Vehicle", "Driver"]}
          filename="Delivery Challan List"
          paginationInfo={paginationInfo}
          controller={controller}
          handleChangePage={handleChangePage}
        />
        <Paper sx={{ width: "100%" }}>
          <TableContainer component={Paper} className={`${styles.list_tbl_container}`}>
            <Table size="small" stickyHeader>
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell align="left" className={`${styles.table_head}`} sx={{ width: "15%" }} onClick={() => HandleSort("entry_no")}>
                    DC No
                    <span className={`${styles.sort_icon}`}>{sortBy === "entry_no" ? "▲" : "▼"}</span>
                  </StyledTableCell>
                  <StyledTableCell align="left" className={`${styles.table_head}`} sx={{ width: "15%" }} onClick={() => HandleSort("entry_date")}>
                    DC Date
                    <span className={`${styles.sort_icon}`}>{sortBy === "entry_date" ? "▲" : "▼"}</span>
                  </StyledTableCell>
                  <StyledTableCell align="left" className={`${styles.table_head}`} sx={{ width: "15%" }} onClick={() => HandleSort("party_name")}>
                    Party Name
                    <span className={`${styles.sort_icon}`}>{sortBy === "party_name" ? "▲" : "▼"}</span>
                  </StyledTableCell>
                  <StyledTableCell align="center" className={`${styles.table_head}`} sx={{ width: "15%" }} onClick={() => HandleSort("totalCylinders")}>
                    No Of Cylinders
                    <span className={`${styles.sort_icon}`}>{sortBy === "totalCylinders" ? "▲" : "▼"}</span>
                  </StyledTableCell>
                  <StyledTableCell align="left" className={`${styles.table_head}`} sx={{ width: "15%" }} onClick={() => HandleSort("vehicle_no")}>
                    Vehicle No
                    <span className={`${styles.sort_icon}`}>{sortBy === "vehicle_no" ? "▲" : "▼"}</span>
                  </StyledTableCell>
                  <StyledTableCell align="left" className={`${styles.table_head}`} sx={{ width: "15%" }} onClick={() => HandleSort("driver_name")}>
                    Driver Name
                    <span className={`${styles.sort_icon}`}>{sortBy === "driver_name" ? "▲" : "▼"}</span>
                  </StyledTableCell>

                  {/* <StyledTableCell align="left" className={`${styles.table_head}`} sx={{ width: "15%" }} onClick={() => HandleSort("driver_name")}>
                  Flag
                    <span className={`${styles.sort_icon}`}>{sortBy === "cancel" ? "▲" : "▼"}</span>
                  </StyledTableCell> */}
  <StyledTableCell align="left" className={`${styles.table_head}`} sx={{ width: "15%" }}>
                    Product Code
                  </StyledTableCell>
                  <StyledTableCell align="center" className={`${styles.table_head}`} sx={{ width: "10%" }}>
                    Action
                  </StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody sx={{ backgroundColor: "#fff" }}>
                {isLoading ? (
                  <StyledTableRow>
                    <StyledTableCell colSpan={7} align="center">
                      Loading...
                    </StyledTableCell>
                  </StyledTableRow>
                ) : Array.isArray(rows) && rows.length > 0 ? (
                  rows.map((row, index) => (
                  <StyledTableRow
  key={row.entry_id}
  sx={{
    "&:last-child td, &:last-child th": { border: 0 },
    backgroundColor: row.status.toLowerCase() === "cancel" ? "#FF0000" : "#fff",
  }}
>
                      <StyledTableCell align="left" className={`${styles.list_table_body}`}>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: "bold",
                            cursor: "pointer",
                            color: "#1976d2",
                            textDecoration: "underline",
                          }}
                          onClick={() => handleOpenDialog(row)}
                        >
                          {row.entry_no || "N/A"}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell align="left" className={`${styles.list_table_body}`}>
                        {dateConversion(row.entry_date) || "N/A"}
                      </StyledTableCell>
                      <StyledTableCell align="left" className={`${styles.list_table_body}`}>
                        {row.type === "customer" ? row.customer_name : row.depot_name}
                      </StyledTableCell>
                      <StyledTableCell align="center" className={`${styles.list_table_body}`}>
                        <Tooltip title="Click here to view cylinder details" arrow>
                          <span
                            onClick={() => handleCylinderCountClick(row)}
                            style={{ cursor: "pointer" }}
                          >
                            {getCylinderCount(row.table_details)}
                          </span>
                        </Tooltip>
                      </StyledTableCell>
                      <StyledTableCell align="left" className={`${styles.list_table_body}`}>
                        {row.vehicle_no || row.unregistered_vehicle || "N/A"}
                      </StyledTableCell>
                      <StyledTableCell align="left" className={`${styles.list_table_body}`}>
                     {row.driver_name || row.unregistered_driver || "N/A"}
                      </StyledTableCell>
                       <StyledTableCell align="center" className={`${styles.list_table_body}`}>
                        {getProductCodesSummaryForRow(row.table_details)}
                      </StyledTableCell>

                      {/* <StyledTableCell align="left" className={styles.list_table_body}>
  <span
    style={{
      color: row.update_check === 0 ? 'green' : 'red',
      fontWeight: 'bold',
    }}
  >
    {row.update_check === 0 ? '' : 'Cancelled'}
  </span>
</StyledTableCell> */}

                      <StyledTableCell align="center">
                        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                          <CustomePopover
                            row={{
                              ...row,
                              totalCylinders: calculateTotalCylinders(row.table_details),
                              totalQuantity: calculateTotalQuantity(row.table_details),
                              productsSummary: calculateProductSummary(row.table_details),
                            }}
                            handleDelete={handleDelete}
                            handleCancel={row.status.toLowerCase() !== "cancel" ? () => handleCancel(row) : false}
                            linkTo="/LogisticDistribution/add-dc"
                            PrintComponent={DeliveryChallanPrint}
                            showEdit={true}
                          />
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <StyledTableRow>
                    <StyledTableCell colSpan={7} align="center">
                      No data available
                    </StyledTableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
                               <CustomPagination
  page={controller.page}
  rowsPerPage={controller.rowsPerPage}
  totalCount={total_count || 0}
  onPageChange={(newPage) => handleChangePage(null, newPage)}
/>
        </Paper>

        {/* ── DELIVERY CHALLAN DETAILS (Redesigned like Sales Order) ── */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth={false}
          sx={{
            "& .MuiDialog-paper": {
              margin: "auto",
              width: "92vw",
              maxHeight: "85vh",
              boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
              overflow: "hidden",
              fontFamily: "'Inter', Arial, sans-serif",
          
            },
          }}
        >
          {/* ── Dialog Header (Navy) ── */}
          <Box sx={{ background: "#1C3A5E", padding: "14px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#fff", letterSpacing: "0.5px", fontFamily: "'Inter', Arial, sans-serif" }}>
              DELIVERY CHALLAN DETAILS
            </Typography>
            <IconButton onClick={handleCloseDialog} sx={{ color: "#fff", p: 0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <DialogContent sx={{ padding: "20px 24px", background: "#F4F7FB", overflowY: "auto" }}>

            {/* ── DC Information Card ── */}
            <Box sx={{ background: "#fff", border: "1px solid #E2E8F0",  overflow: "hidden", mb: 2.5 }}>
              <Box sx={{ background: "#2A4F7A", padding: "9px 18px" }}>
                <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#fff", fontFamily: "'Inter', Arial, sans-serif", letterSpacing: "0.4px" }}>
                  📋  DC Information
                </Typography>
              </Box>

              <Box>
                <Grid container>
                  {/* Row 1 */}
                  <Grid item xs={12} sm={6} md={3} sx={{ borderRight: "1px solid #EDF2F7", borderBottom: "1px solid #EDF2F7", padding: "12px 16px" }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.5, fontFamily: "'Inter', Arial, sans-serif" }}>Entry No</Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#1C3A5E", fontFamily: "'Inter', Arial, sans-serif" }}>{selectedRow?.entry_no || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} sx={{ borderRight: "1px solid #EDF2F7", borderBottom: "1px solid #EDF2F7", padding: "12px 16px" }}>
  <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.5, fontFamily: "'Inter', Arial, sans-serif" }}>Party Name</Typography>
  <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#1C3A5E", fontFamily: "'Inter', Arial, sans-serif" }}>{(selectedRow?.type === "customer" ? selectedRow?.customer_name : selectedRow?.depot_name) || "N/A"}</Typography>
</Grid>
                  <Grid item xs={12} sm={6} md={3} sx={{ borderRight: "1px solid #EDF2F7", borderBottom: "1px solid #EDF2F7", padding: "12px 16px" }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.5, fontFamily: "'Inter', Arial, sans-serif" }}>Entry Date</Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#2D3748", fontFamily: "'Inter', Arial, sans-serif" }}>{dateConversion(selectedRow?.entry_date) || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} sx={{ borderRight: "1px solid #EDF2F7", borderBottom: "1px solid #EDF2F7", padding: "12px 16px" }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.5, fontFamily: "'Inter', Arial, sans-serif" }}>Driver Name</Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#2D3748", fontFamily: "'Inter', Arial, sans-serif" }}>{selectedRow?.driver_name || selectedRow?.unregistered_driver || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} sx={{ borderBottom: "1px solid #EDF2F7", padding: "12px 16px" }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.5, fontFamily: "'Inter', Arial, sans-serif" }}>Vehicle No</Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#2D3748", fontFamily: "'Inter', Arial, sans-serif" }}>{selectedRow?.vehicle_no || selectedRow?.unregistered_vehicle || "N/A"}</Typography>
                  </Grid>

                  {/* Row 2 */}
                  <Grid item xs={12} sm={6} md={3} sx={{ borderRight: "1px solid #EDF2F7", borderBottom: "1px solid #EDF2F7", padding: "12px 16px" }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.5, fontFamily: "'Inter', Arial, sans-serif" }}>Delivery Address</Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#2D3748", fontFamily: "'Inter', Arial, sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{selectedRow?.delivery_address || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} sx={{ borderRight: "1px solid #EDF2F7", borderBottom: "1px solid #EDF2F7", padding: "12px 16px" }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.5, fontFamily: "'Inter', Arial, sans-serif" }}>Contact No</Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#2D3748", fontFamily: "'Inter', Arial, sans-serif" }}>{selectedRow?.contact_no || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} sx={{ borderRight: "1px solid #EDF2F7", borderBottom: "1px solid #EDF2F7", padding: "12px 16px" }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.5, fontFamily: "'Inter', Arial, sans-serif" }}>PO No</Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#2D3748", fontFamily: "'Inter', Arial, sans-serif" }}>{selectedRow?.po_no || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} sx={{ borderBottom: "1px solid #EDF2F7", padding: "12px 16px" }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.5, fontFamily: "'Inter', Arial, sans-serif" }}>PO Date</Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#2D3748", fontFamily: "'Inter', Arial, sans-serif" }}>{dateConversion(selectedRow?.po_date) || "N/A"}</Typography>
                  </Grid>

                  {/* Row 3 */}
                  <Grid item xs={12} sm={6} md={3} sx={{ borderRight: "1px solid #EDF2F7", borderBottom: "1px solid #EDF2F7", padding: "12px 16px" }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.5, fontFamily: "'Inter', Arial, sans-serif" }}>SO No</Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#2D3748", fontFamily: "'Inter', Arial, sans-serif" }}>{selectedRow?.so_no || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} sx={{ borderRight: "1px solid #EDF2F7", borderBottom: "1px solid #EDF2F7", padding: "12px 16px" }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.5, fontFamily: "'Inter', Arial, sans-serif" }}>SO Date</Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#2D3748", fontFamily: "'Inter', Arial, sans-serif" }}>{dateConversion(selectedRow?.so_date) || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} sx={{ borderRight: "1px solid #EDF2F7", borderBottom: "1px solid #EDF2F7", padding: "12px 16px" }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.5, fontFamily: "'Inter', Arial, sans-serif" }}>Invoice No</Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#2D3748", fontFamily: "'Inter', Arial, sans-serif" }}>{selectedRow?.invoice_no || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} sx={{ borderBottom: "1px solid #EDF2F7", padding: "12px 16px" }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.5, fontFamily: "'Inter', Arial, sans-serif" }}>Prepared By</Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#2D3748", fontFamily: "'Inter', Arial, sans-serif" }}>{selectedRow?.prepared_by || "N/A"}</Typography>
                  </Grid>

                  {/* Row 4 */}
                  <Grid item xs={12} sm={6} md={3} sx={{ borderRight: "1px solid #EDF2F7", padding: "12px 16px" }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.5, fontFamily: "'Inter', Arial, sans-serif" }}>Product Code</Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#2D3748", fontFamily: "'Inter', Arial, sans-serif" }}>{selectedRow?.product_code || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6} sx={{ borderRight: "1px solid #EDF2F7", padding: "12px 16px" }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.5, fontFamily: "'Inter', Arial, sans-serif" }}>Product Description</Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#2D3748", fontFamily: "'Inter', Arial, sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{selectedRow?.prod_description || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} sx={{ padding: "12px 16px" }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.5, fontFamily: "'Inter', Arial, sans-serif" }}>Status</Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: 700, fontFamily: "'Inter', Arial, sans-serif", color: selectedRow?.status?.toLowerCase() === "cancel" ? "#E53E3E" : "#38A169" }}>
                      {selectedRow?.status?.toLowerCase() === "cancel" ? "Cancelled" : "Active"}
                    </Typography>
                  </Grid>

                </Grid>
              </Box>
            </Box>

            {/* ── Cylinder Details Table ── */}
            <Box sx={{ background: "#fff", border: "1px solid #E2E8F0",  overflow: "hidden" }}>
              

              <TableContainer sx={{ maxHeight: "320px" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <StyledTableRow>
 {["Sr No", "Cylinder No","Tag No","owner", ...(selectedRow?.type !== "depot" ? ["Actual Qty", "Billing Qty"] : []), "UOM"].map((header) => (
                        <StyledTableCell
                          key={header}
                          align="center"
                          sx={{
                            backgroundColor: "#1C3A5E !important",
                            color: "#fff !important",
                            fontFamily: "'Inter', Arial, sans-serif",
                            fontWeight: 700,
                            fontSize: "12px",
                            padding: "10px 12px",
                            whiteSpace: "nowrap",
                            borderRight: "1px solid rgba(255,255,255,0.1)",
                          }}
                        >
                          {header}
                        </StyledTableCell>
                      ))}
                    </StyledTableRow>
                  </TableHead>
                  <TableBody>
                    {selectedCylinderDetails.length > 0 ? (
                      selectedCylinderDetails.map((row, index) => (
                        <StyledTableRow
                          key={index}
                          sx={{
                            backgroundColor: index % 2 === 0 ? "#fff" : "#F8FAFC",
                            "&:hover": { backgroundColor: "#EBF4FF" },
                          }}
                        >
                          {[
                            index + 1,
                            row.cyl_no || "N/A",
                             row.tag_no || "N/A",
                             row.owner ||"N/A",
                            ...(selectedRow?.type !== "depot" ? [row.qty || "N/A", row.bill_qty || "N/A"] : []),
                            row.uom || "N/A"
                           
                          ].map((val, ci) => (
                            <StyledTableCell
                              key={ci}
                              align="center"
                              sx={{
                                fontSize: "12px",
                                fontFamily: "'Inter', Arial, sans-serif",
                                color: "#2D3748",
                                padding: "8px 12px",
                                borderBottom: "1px solid #EDF2F7",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {val}
                            </StyledTableCell>
                          ))}
                        </StyledTableRow>
                      ))
                    ) : (
                      <StyledTableRow>
                        <StyledTableCell
                          colSpan={6}
                          align="center"
                          sx={{ py: 5, fontSize: "13px", color: "#A0AEC0", fontFamily: "'Inter', Arial, sans-serif" }}
                        >
                          No cylinder details available
                        </StyledTableCell>
                      </StyledTableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

          </DialogContent>

          {/* ── Dialog Footer ── */}
          <Box sx={{ padding: "12px 24px", background: "#F8FAFC", borderTop: "1px solid #E2E8F0", display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={handleCloseDialog}
              sx={{
                background: "#E53E3E",
                color: "#fff",
                fontSize: "13px",
                fontWeight: 600,
                textTransform: "none",
                padding: "7px 20px",
                borderRadius: "8px",
                fontFamily: "'Inter', Arial, sans-serif",
                "&:hover": { background: "#971f16" },
              }}
            >
              Close
            </Button>
          </Box>
        </Dialog>

        <Dialog
          open={openCylinderDialog}
          onClose={handleCloseCylinderDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            {dialogTitle}
            <IconButton aria-label="close" onClick={handleCloseCylinderDialog}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Table size="small">
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell>Cylinder No</StyledTableCell>
                  <StyledTableCell>Tag No</StyledTableCell>
                  <StyledTableCell>Owner</StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {selectedCylinderDetails.length > 0 ? (
                  selectedCylinderDetails.map((cylinder, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell>{cylinder.cyl_no}</StyledTableCell>
                      <StyledTableCell>{cylinder.tag_no}</StyledTableCell>
                      <StyledTableCell>{cylinder.owner}</StyledTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <StyledTableRow>
                    <StyledTableCell colSpan={3} align="center">
                      No cylinder details available
                    </StyledTableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </DialogContent>
        </Dialog>
        <DeleteDialog
          open={isDelete}
          handleClose={handleClose}
          deleteInfo={deleteInfo}
          deleteRecord={deleteRecord}
          deleteID={deleteInfo?.entry_id || null}
          displayName={deleteInfo?.entry_no || ""}
        />
        <CancelDialog
          open={isCancel}
          handleClose={handleCloseCancel}
          cancelInfo={cancelInfo}
          cancelRecord={cancelRecord}
          cancelID={cancelInfo?.entry_id || null}
          displayName={cancelInfo?.entry_no || ""}
        />
        <ReusableSnackbar
          open={!!successMessage}
          onClose={(event, reason) => {
            if (reason !== 'clickaway') {
              setSuccessMessage("");
            }
          }}
          message={successMessage}
          autoHideDuration={2000}
          vertical="top"
          horizontal="right"
        />
      </Box>
    </Box>
  );
};

export default DeliveryChallanList;